import { FC, useEffect } from 'react'
import { LoginCallback, useOktaAuth } from '@okta/okta-react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { Button, SpaceBetween } from '@cloudscape-design/components'
import LoadingSpinner from '../../components/LoadingSpinner'

const OKTA_ORIGINAL_URI_STORAGE_KEY = 'ssp.okta.originalUri'

const getTargetPath = (searchParams: URLSearchParams) => {
	const rawPath = searchParams.get('path')
	if (!rawPath) {
		return ''
	}

	const pathname = rawPath.startsWith('/') ? rawPath : `/${rawPath}`
	const targetSearchParams = new URLSearchParams(searchParams)
	targetSearchParams.delete('path')
	targetSearchParams.delete('code')
	targetSearchParams.delete('state')

	const queryString = targetSearchParams.toString()
	return queryString ? `${pathname}?${queryString}` : pathname
}

const RootRedirect: FC = () => {
	const { oktaAuth, authState } = useOktaAuth()
	const [searchParams] = useSearchParams()
	const targetPath = getTargetPath(searchParams)

	useEffect(() => {
		if (!authState || authState.isAuthenticated || !targetPath) {
			return
		}

		window.sessionStorage.setItem(OKTA_ORIGINAL_URI_STORAGE_KEY, targetPath)
		oktaAuth.setOriginalUri(targetPath)
		oktaAuth.signInWithRedirect()
	}, [oktaAuth, authState, targetPath])

	// Loading component while Okta authentication is in progress
	// searchParams.get('code') is used to check if the user is being redirected back from Okta
	// e.g: http://localhost:3000/index.html?code=xxxxxxxxxxxxxxxxxxxx&state=xxxxxxxxxxxxxxxxxxxx
	if (!authState || searchParams.get('code')) {
		return (
			<>
				<LoginCallback />
				<LoadingSpinner />
			</>
		)
	}

	// Redirect to dashboard if user is authenticated
	if (authState.isAuthenticated) {
		// Redirect to the path specified in the URL query string
		if (targetPath) {
			return <Navigate to={targetPath} replace />
		}

		return <Navigate to="/dashboard" />
	}

	if (targetPath) {
		return <LoadingSpinner />
	}

	// Displays login button if user is not authenticated
	return (
		<SpaceBetween direction="vertical" size="l">
			<h1>Welcome to Globe SSP</h1>
			<Button onClick={() => oktaAuth.signInWithRedirect()}> Login </Button>
		</SpaceBetween>
	)
}

export default RootRedirect
